import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Mail, Bell, CheckCircle, XCircle, Clock, Send, ClipboardList, FileSignature, CalendarDays, Calendar, List, MapPin, Smartphone, AlertTriangle, Building2, Star, Sparkles, Target, PartyPopper, Stethoscope, ChevronRight } from 'lucide-react';
import CalendarAgendaView from './CalendarAgendaView';
import useRecetas from '../hooks/usePuedeEmitirRecetas';
import AppointmentNoteEditor from './AppointmentNoteEditor';
import ConfirmModal from './ConfirmModal';
import { getExtraLabel, formatExtraValue } from '../utils/patientExtraData';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

interface NotificationLog {
  status: string;
  template_name: string;
  created_at: string;
  error_message: string | null;
}

interface CalendarAppointment {
  id: number;
  professional_id: number;
  client_name?: string;
  client_display_name: string;
  client_email?: string;
  client_phone?: string;
  service_name: string;
  service_category: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  price: number;
  deposit_required: boolean;
  deposit_amount?: number;
  deposit_paid: boolean;
  notes?: string;
  professional_notes?: string;
  is_first_time: boolean;
  urgency_level: 'low' | 'normal' | 'high';
  created_at: string;
  updated_at: string;
  cancellation_reason?: string;
  confirmed_by?: string;
  confirmed_at?: string;
  cancelled_by?: string;
  cancelled_at?: string;
  location_id?: number;
  location_name?: string;
  location_city?: string;
  patient_extra_data?: Record<string, any>;
  is_override?: boolean;
  metodo_pago_elegido?: string;
  pago_verificado?: boolean;
  // Notification tracking
  reminder_sent?: boolean;
  reminder_sent_at?: string;
  whatsapp_confirmation?: NotificationLog | null;
  whatsapp_reminder?: NotificationLog | null;
  email_confirmation?: NotificationLog | null;
  email_reminder?: NotificationLog | null;
  // Manual WhatsApp (secretaria envía por WhatsApp Web) — status: 'sent' | 'confirmed' | 'no_response' | 'cancelled'
  manual_whatsapp?: NotificationLog | null;
}

// Helper: mostrar nombre multi-servicio si existe
const getServiceDisplay = (apt: CalendarAppointment): string => {
  try {
    const extra = apt.patient_extra_data;
    if (extra && extra.all_service_names) return extra.all_service_names;
  } catch (e) {}
  return apt.service_name || 'Consulta';
};

interface ProfessionalSchedule {
  id: number;
  professional_id: number;
  day_of_week: number; // 0=Domingo, 1=Lunes, ..., 6=Sábado
  start_time: string;
  end_time: string;
  is_available: boolean;
}

interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type: 'salud' | 'belleza';
  especialidad: string;
  business_name?: string;
  picture?: string;
  cover_photo?: string;
}

interface ThemeConfig {
  gradient: string;
  bgGradient: string;
  primary: string;
  title: string;
  icons: {
    calendar: React.ReactNode;
    today: React.ReactNode;
    prev: string;
    next: string;
    add: string;
    list: React.ReactNode;
    grid: React.ReactNode;
    confirmed: React.ReactNode;
    pending: React.ReactNode;
    completed: React.ReactNode;
    cancelled: React.ReactNode;
    urgent: React.ReactNode;
  };
}

interface DashboardStats {
  todayAppointments: number;
  pendingAppointments: number;
  confirmedAppointments: number;
  totalRevenue: number;
  urgentCases: number;
}

type ViewMode = 'agenda' | 'month' | 'week' | 'day' | 'list';
type FilterStatus = 'todos' | 'pending' | 'confirmed' | 'completed' | 'cancelled';

// =====================================================
// CONFIGURACIÓN
// =====================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const DEBUG_MODE = process.env.NODE_ENV === 'development';

const debugLog = (message: string, data?: any) => {
  if (DEBUG_MODE) {
    console.log(`[AdvancedCalendar] ${message}`, data || '');
  }
};

// =====================================================
// FUNCIONES DE UTILIDAD (ACTUALIZADAS)
// =====================================================

const getToday = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatLocalDate = (dateString: string): string => {
  try {
    let year: number, month: number, day: number;
    
    if (dateString.includes('T')) {
      const datePart = dateString.split('T')[0];
      const parts = datePart.split('-').map(Number);
      year = parts[0];
      month = parts[1] - 1;
      day = parts[2];
    } else if (dateString.includes('-')) {
      const parts = dateString.split('-').map(Number);
      year = parts[0];
      month = parts[1] - 1;
      day = parts[2];
    } else {
      const date = new Date(dateString);
      year = date.getFullYear();
      month = date.getMonth();
      day = date.getDate();
    }
    
    const date = new Date(year, month, day, 12, 0, 0);
    
    if (isNaN(date.getTime())) {
      console.error('Fecha inválida:', dateString);
      return 'Fecha inválida';
    }
    
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formateando fecha:', dateString, error);
    return 'Fecha inválida';
  }
};

const getDateOnly = (dateString: string): string => {
  try {
    if (dateString.includes('T')) {
      return dateString.split('T')[0];
    }
    return dateString;
  } catch (error) {
    console.error('Error extrayendo fecha:', dateString, error);
    return dateString;
  }
};

const getStatusStyle = (status: string): string => {
  switch (status) {
    case 'confirmed': return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200';
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200';
    case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200';
    case 'cancelled': return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200';
    case 'no_show': return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200';
  }
};

const getStatusText = (status: string): string => {
  const statusMap = {
    pending: 'Pendiente',
    confirmed: 'Confirmada',
    completed: 'Completada',
    cancelled: 'Cancelada',
    no_show: 'No asistió'
  };
  return statusMap[status as keyof typeof statusMap] || status;
};

const formatPrice = (price: number): string => {
  return price.toLocaleString('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0
  });
};

// =====================================================
// CONFIGURACIÓN DE TEMAS
// =====================================================

const getThemeConfig = (professionalType: 'salud' | 'belleza'): ThemeConfig => {
  if (professionalType === 'belleza') {
    return {
      gradient: 'from-pink-500 to-purple-500',
      bgGradient: 'from-pink-50 to-purple-50',
      primary: 'pink',
      title: 'Agenda de Belleza',
      icons: {
        calendar: <Sparkles className="w-6 h-6 inline text-pink-500" />,
        today: <Star className="w-4 h-4 inline text-yellow-500" />,
        prev: '←',
        next: '→',
        add: '+',
        list: <ClipboardList className="w-4 h-4" />,
        grid: <CalendarDays className="w-4 h-4" />,
        confirmed: <CheckCircle className="w-4 h-4 inline text-green-600" />,
        pending: <Clock className="w-4 h-4 inline text-orange-500" />,
        completed: <PartyPopper className="w-4 h-4 inline text-blue-600" />,
        cancelled: <XCircle className="w-4 h-4 inline text-red-600" />,
        urgent: <AlertTriangle className="w-4 h-4 inline text-red-600" />
      }
    };
  } else {
    return {
      gradient: 'from-blue-500 to-green-500',
      bgGradient: 'from-blue-50 to-green-50',
      primary: 'blue',
      title: 'Agenda Médica',
      icons: {
        calendar: <Stethoscope className="w-6 h-6 inline text-blue-500" />,
        today: <MapPin className="w-4 h-4 inline text-blue-500" />,
        prev: '←',
        next: '→',
        add: '+',
        list: <ClipboardList className="w-4 h-4" />,
        grid: <CalendarDays className="w-4 h-4" />,
        confirmed: <CheckCircle className="w-4 h-4 inline text-green-600" />,
        pending: <Clock className="w-4 h-4 inline text-orange-500" />,
        completed: <Target className="w-4 h-4 inline text-blue-600" />,
        cancelled: <XCircle className="w-4 h-4 inline text-red-600" />,
        urgent: <AlertTriangle className="w-4 h-4 inline text-red-600" />
      }
    };
  }
};

// =====================================================
// FUNCIONES DE API
// =====================================================

const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  debugLog(`API Request: ${options.method || 'GET'} ${url}`, options.body || '');

  // Usar authFetch para manejo automático de token refresh
  const { authFetch: authFetchFn } = await import('../services/api');
  const response = await authFetchFn(url, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    debugLog(`API Error ${response.status}:`, errorData);

    if (response.status === 401) {
      throw new Error('Sesión expirada');
    }

    throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  const result = await response.json();
  debugLog('API Response:', result);
  return result;
};

// =====================================================
// COMPONENTE CALENDARIO PERSONALIZADO
// =====================================================

interface CustomCalendarProps {
  currentDate: Date;
  appointments: CalendarAppointment[];
  schedule: ProfessionalSchedule[];
  onDateClick: (date: Date) => void;
  onAppointmentClick: (appointment: CalendarAppointment) => void;
  onShowDayAppointments: (appointments: CalendarAppointment[]) => void;
  theme: ThemeConfig;
  maxOverridesPerSlot?: number;
  onAddAppointment?: (date: string, time: string) => void;
  blockedDates?: string[]; // YYYY-MM-DD con horarios bloqueados
}

// =====================================================
// COMPONENTE DE INDICADORES DE NOTIFICACIÓN
// =====================================================
const NotificationIndicators: React.FC<{ appointment: CalendarAppointment; compact?: boolean }> = ({ appointment, compact = false }) => {
  const apt = appointment;

  if (compact) {
    // Vista compacta: solo iconos pequeños en fila
    return (
      <div className="flex items-center gap-1 mt-1">
        {/* WhatsApp Confirmation */}
        {apt.whatsapp_confirmation ? (
          apt.whatsapp_confirmation.status === 'sent' ? (
            <MessageCircle className="w-3 h-3 text-green-500" />
          ) : (
            <MessageCircle className="w-3 h-3 text-red-400" />
          )
        ) : null}

        {/* WhatsApp Reminder */}
        {apt.whatsapp_reminder ? (
          apt.whatsapp_reminder.status === 'sent' ? (
            <Bell className="w-3 h-3 text-blue-500" />
          ) : (
            <Bell className="w-3 h-3 text-red-400" />
          )
        ) : apt.reminder_sent ? (
          <Bell className="w-3 h-3 text-blue-500" />
        ) : null}

        {/* Manual WhatsApp (secretaria) */}
        {apt.manual_whatsapp ? (
          apt.manual_whatsapp.status === 'confirmed' ? (
            <MessageCircle className="w-3 h-3 text-emerald-600" />
          ) : apt.manual_whatsapp.status === 'cancelled' ? (
            <MessageCircle className="w-3 h-3 text-red-500" />
          ) : apt.manual_whatsapp.status === 'no_response' ? (
            <MessageCircle className="w-3 h-3 text-amber-500" />
          ) : (
            <MessageCircle className="w-3 h-3 text-sky-500" />
          )
        ) : null}

        {/* Email Confirmation */}
        {apt.email_confirmation ? (
          apt.email_confirmation.status === 'sent' ? (
            <Mail className="w-3 h-3 text-emerald-500" />
          ) : (
            <Mail className="w-3 h-3 text-red-400" />
          )
        ) : null}

        {/* Email Reminder */}
        {apt.email_reminder ? (
          apt.email_reminder.status === 'sent' ? (
            <Send className="w-3 h-3 text-teal-500" />
          ) : (
            <Send className="w-3 h-3 text-red-400" />
          )
        ) : null}
      </div>
    );
  }

  // Vista detallada con texto
  return (
    <div className="space-y-1.5 mt-3">
      {/* WhatsApp Confirmation */}
      {apt.whatsapp_confirmation ? (
        apt.whatsapp_confirmation.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-green-600"
            title={`Enviado: ${new Date(apt.whatsapp_confirmation.created_at).toLocaleString('es-AR')}`}>
            <MessageCircle className="w-3.5 h-3.5" />
            <CheckCircle className="w-3 h-3" />
            <span>WA confirmación enviada</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500"
            title={apt.whatsapp_confirmation.error_message || 'Error al enviar'}>
            <MessageCircle className="w-3.5 h-3.5" />
            <XCircle className="w-3 h-3" />
            <span>Error WA confirmación</span>
          </div>
        )
      ) : apt.client_phone ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <MessageCircle className="w-3.5 h-3.5" />
          <Clock className="w-3 h-3" />
          <span>Sin confirmación WA</span>
        </div>
      ) : null}

      {/* WhatsApp Reminder */}
      {apt.whatsapp_reminder ? (
        apt.whatsapp_reminder.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-blue-600"
            title={`Enviado: ${new Date(apt.whatsapp_reminder.created_at).toLocaleString('es-AR')}`}>
            <Bell className="w-3.5 h-3.5" />
            <CheckCircle className="w-3 h-3" />
            <span>Recordatorio WA enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500"
            title={apt.whatsapp_reminder.error_message || 'Error'}>
            <Bell className="w-3.5 h-3.5" />
            <XCircle className="w-3 h-3" />
            <span>Error recordatorio WA</span>
          </div>
        )
      ) : apt.reminder_sent ? (
        <div className="flex items-center gap-1.5 text-xs text-blue-600"
          title={apt.reminder_sent_at ? `Enviado: ${new Date(apt.reminder_sent_at).toLocaleString('es-AR')}` : ''}>
          <Bell className="w-3.5 h-3.5" />
          <CheckCircle className="w-3 h-3" />
          <span>Recordatorio enviado</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Bell className="w-3.5 h-3.5" />
          <Clock className="w-3 h-3" />
          <span>Recordatorio pendiente</span>
        </div>
      )}

      {/* Manual WhatsApp (secretaria) */}
      {apt.manual_whatsapp ? (
        apt.manual_whatsapp.status === 'confirmed' ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600"
            title={`Cliente confirmó: ${new Date(apt.manual_whatsapp.created_at).toLocaleString('es-AR')}`}>
            <MessageCircle className="w-3.5 h-3.5" />
            <CheckCircle className="w-3 h-3" />
            <span>Confirmó por WhatsApp</span>
          </div>
        ) : apt.manual_whatsapp.status === 'cancelled' ? (
          <div className="flex items-center gap-1.5 text-xs text-red-500"
            title={`Canceló: ${new Date(apt.manual_whatsapp.created_at).toLocaleString('es-AR')}`}>
            <MessageCircle className="w-3.5 h-3.5" />
            <XCircle className="w-3 h-3" />
            <span>Canceló por WhatsApp</span>
          </div>
        ) : apt.manual_whatsapp.status === 'no_response' ? (
          <div className="flex items-center gap-1.5 text-xs text-amber-600"
            title={`Sin respuesta: ${new Date(apt.manual_whatsapp.created_at).toLocaleString('es-AR')}`}>
            <MessageCircle className="w-3.5 h-3.5" />
            <Clock className="w-3 h-3" />
            <span>Sin respuesta (WA manual)</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-sky-600"
            title={`Enviado manual: ${new Date(apt.manual_whatsapp.created_at).toLocaleString('es-AR')}`}>
            <MessageCircle className="w-3.5 h-3.5" />
            <Send className="w-3 h-3" />
            <span>Enviado por secretaria (WA)</span>
          </div>
        )
      ) : null}

      {/* Email Confirmation */}
      {apt.email_confirmation ? (
        apt.email_confirmation.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-600"
            title={`Enviado: ${new Date(apt.email_confirmation.created_at).toLocaleString('es-AR')}`}>
            <Mail className="w-3.5 h-3.5" />
            <CheckCircle className="w-3 h-3" />
            <span>Email confirmación enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500"
            title={apt.email_confirmation.error_message || 'Error al enviar'}>
            <Mail className="w-3.5 h-3.5" />
            <XCircle className="w-3 h-3" />
            <span>Error email confirmación</span>
          </div>
        )
      ) : apt.client_email ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Mail className="w-3.5 h-3.5" />
          <Clock className="w-3 h-3" />
          <span>Sin email confirmación</span>
        </div>
      ) : null}

      {/* Email Reminder */}
      {apt.email_reminder ? (
        apt.email_reminder.status === 'sent' ? (
          <div className="flex items-center gap-1.5 text-xs text-teal-600"
            title={`Enviado: ${new Date(apt.email_reminder.created_at).toLocaleString('es-AR')}`}>
            <Send className="w-3.5 h-3.5" />
            <CheckCircle className="w-3 h-3" />
            <span>Email recordatorio enviado</span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-red-500"
            title={apt.email_reminder.error_message || 'Error'}>
            <Send className="w-3.5 h-3.5" />
            <XCircle className="w-3 h-3" />
            <span>Error email recordatorio</span>
          </div>
        )
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Send className="w-3.5 h-3.5" />
          <Clock className="w-3 h-3" />
          <span>Email rec. pendiente</span>
        </div>
      )}
    </div>
  );
};

const CustomCalendar: React.FC<CustomCalendarProps> = ({
  currentDate,
  appointments,
  schedule,
  onDateClick,
  onAppointmentClick,
  onShowDayAppointments,
  theme,
  maxOverridesPerSlot = 0,
  onAddAppointment,
  blockedDates = []
}) => {
  const blockedSet = new Set(blockedDates);
  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
 
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());
 
  const isDayAvailable = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    return schedule.some(s => s.day_of_week === dayOfWeek && s.is_available);
  };
 
  const days = [];
  const currentCalendarDate = new Date(startDate);
 
  for (let i = 0; i < 42; i++) {
    const date = new Date(currentCalendarDate);
    const isCurrentMonth = date.getMonth() === month;
    const isToday = date.toDateString() === today.toDateString();
    const dateStr = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    const isAvailable = isDayAvailable(date);
   
    const dayAppointments = appointments.filter(apt => getDateOnly(apt.appointment_date) === dateStr);
   
    days.push({
      date: new Date(date),
      day: date.getDate(),
      isCurrentMonth,
      isToday,
      isAvailable,
      appointments: dayAppointments,
      isBlocked: blockedSet.has(dateStr)
    });
   
    currentCalendarDate.setDate(currentCalendarDate.getDate() + 1);
  }
 
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
 
  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
      <div className="grid grid-cols-7 gap-0 border-b border-gray-200 bg-gray-50/50">
        {dayNames.map((day, i) => (
          <div key={day} className={`py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-widest ${i < 6 ? 'border-r border-gray-200' : ''}`}>
            {day}
          </div>
        ))}
      </div>
     
      <div className="grid grid-cols-7 gap-0">
        {days.map((dayInfo, index) => (
          <div
            key={index}
            onClick={() => {
              onDateClick(dayInfo.date);
              if (dayInfo.appointments.length > 0) {
                onShowDayAppointments(dayInfo.appointments);
              }
            }}
            className={`min-h-[140px] border-r border-b border-gray-200 p-1.5 cursor-pointer transition-colors relative group ${
              dayInfo.isCurrentMonth
                ? dayInfo.isToday
                  ? 'bg-blue-50/20 hover:bg-blue-50/40' // Fondo súper sutil para hoy
                  : dayInfo.isAvailable
                    ? 'hover:bg-gray-50'
                    : 'bg-red-50/20 hover:bg-red-50/40' // Día no laboral
                : 'bg-gray-50/30 text-gray-300'
            }`}
          >
            {/* Header del Día */}
            <div className="flex items-center justify-between mb-2 p-1">
              <div className={`w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium transition-all ${
                dayInfo.isToday
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20 scale-110' // Estilo Google/Apple para Hoy
                  : dayInfo.isCurrentMonth
                    ? 'text-gray-700 group-hover:bg-gray-200/50'
                    : 'text-gray-400'
              }`}>
                {dayInfo.day}
              </div>
              
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {dayInfo.isCurrentMonth && onAddAppointment && (
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       onAddAppointment(`${dayInfo.date.getFullYear()}-${(dayInfo.date.getMonth() + 1).toString().padStart(2, '0')}-${dayInfo.date.getDate().toString().padStart(2, '0')}`, '09:00');
                     }}
                     title="Añadir cita"
                     className="w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center text-xs transition-colors"
                   >
                     +
                   </button>
                )}
              </div>
            </div>
           
            {/* Chip de horarios bloqueados */}
            {dayInfo.isBlocked && dayInfo.isCurrentMonth && (
              <div
                className="mx-1 mb-1 flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700 text-[10px] font-semibold"
                title="Este día tiene horarios bloqueados"
              >
                <span>🚫</span>
                <span className="truncate">Bloqueado</span>
              </div>
            )}

            {/* Lista de Turnos Modernizada (Pills) */}
            <div className="space-y-1">
              {dayInfo.appointments.slice(0, 4).map(apt => {
                const timeStr = apt.appointment_time.substring(0, 5); // Sin segundos
                const isOverridden = apt.is_override;
                return (
                  <div
                    key={apt.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick(apt);
                    }}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[11.5px] truncate cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-sm hover:z-10 relative border border-transparent ${getStatusStyle(apt.status)}`}
                    title={`${timeStr} - ${apt.client_display_name} (${getServiceDisplay(apt)})`}
                  >
                    {/* Indicador de color/estado */}
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-current opacity-70" />
                    
                    {/* Hora */}
                    <span className="font-semibold flex-shrink-0 tracking-tight">{timeStr}</span>
                    
                    {/* Nombre Píldora */}
                    <span className="truncate">{apt.client_display_name}</span>
                    
                    {/* Distintivos Especiales y Botones alineados a la derecha */}
                    <div className="ml-auto flex items-center space-x-1.5 flex-shrink-0 rounded px-0.5" style={{ background: 'inherit', filter: 'brightness(0.95)' }}>
                      {isOverridden && (
                         <span className="text-[9px] font-bold text-orange-700">ST</span>
                      )}
                      
                      {/* Botón de Sobreturno */}
                      {maxOverridesPerSlot > 0 && onAddAppointment && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const date = apt.appointment_date.split('T')[0];
                            const time = apt.appointment_time.substring(0, 5);
                            onAddAppointment(date, time);
                          }}
                          className="w-[18px] h-[18px] rounded flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white shadow-sm transition-transform hover:scale-110"
                          title="Agregar sobreturno"
                        >
                          <span className="text-[7.5px] font-bold leading-none">+ST</span>
                        </button>
                      )}

                      {apt.urgency_level === 'high' && (
                        <AlertTriangle className="w-[12px] h-[12px] text-red-600" />
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Botón "+X citas más" (Minimalista) */}
              {dayInfo.appointments.length > 4 && (
                <div
                  className="mx-1 mt-1 text-[11px] font-medium text-gray-500 text-center py-1 bg-gray-100/80 hover:bg-gray-200 rounded-md cursor-pointer transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowDayAppointments(dayInfo.appointments);
                  }}
                  title="Ver todas las citas del día"
                >
                  +{dayInfo.appointments.length - 4} más
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

interface AdvancedCalendarProps {
  overrideUser?: User | null;
}

const AdvancedCalendar: React.FC<AdvancedCalendarProps> = ({ overrideUser }) => {
  const navigate = useNavigate();
  const { mostrar: mostrarBotonReceta, puedeEmitir: puedeEmitirRecetas } = useRecetas();
 
  // Estados principales
  const [user, setUser] = useState<User | null>(null);
  const [appointments, setAppointments] = useState<CalendarAppointment[]>([]);
  // Sedes ACTIVAS reales del profesional (para el selector; antes se derivaban
  // de las citas, lo que mostraba sedes inactivas con citas huérfanas y omitía
  // las activas sin citas).
  const [sedes, setSedes] = useState<{ id: number; name: string; city?: string }[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<CalendarAppointment[]>([]);
  const [schedule, setSchedule] = useState<ProfessionalSchedule[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  
  // ✅ MODIFICADO: Recuperar vista guardada de localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const savedView = localStorage.getItem('calendarViewMode');
    return (savedView as ViewMode) || 'agenda';
  });
  
  // ✅ NUEVO: Función para guardar y cambiar vista
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem('calendarViewMode', mode);
  };

  const [filterStatus, setFilterStatus] = useState<FilterStatus>('todos');
  const [filterLocation, setFilterLocation] = useState<number | 'all'>('all');

  // Estados de carga y errores
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
 
  // Estados de modal
  const [selectedAppointment, setSelectedAppointment] = useState<CalendarAppointment | null>(null);
  const [showAppointmentDetail, setShowAppointmentDetail] = useState(false);
  const [confirmHideOpen, setConfirmHideOpen] = useState(false); // "Borrar" cancelado
  const [selectedDayAppointments, setSelectedDayAppointments] = useState<CalendarAppointment[]>([]);
  const [showDayDetail, setShowDayDetail] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [pendingTransferAppointment, setPendingTransferAppointment] = useState<CalendarAppointment | null>(null);

  // Sobreturnos
  const [maxOverridesPerSlot, setMaxOverridesPerSlot] = useState(0);

  // ✅ Fechas con horarios bloqueados (para indicador en mes/lista)
  const [blockedDates, setBlockedDates] = useState<string[]>([]);

  // Estados de estadísticas
  const [stats, setStats] = useState<DashboardStats>({
    todayAppointments: 0,
    pendingAppointments: 0,
    confirmedAppointments: 0,
    totalRevenue: 0,
    urgentCases: 0
  });
 
  const [theme, setTheme] = useState<ThemeConfig>(getThemeConfig('belleza'));

  // =====================================================
  // FUNCIONES DE CARGA DE DATOS
  // =====================================================

  const loadLocations = async (professionalId: number) => {
    try {
      const response = await apiRequest(`/api/locations/professional/${professionalId}`);
      const list = (response && (response.locations || (response.data && response.data.locations))) || [];
      setSedes(
        list
          .filter((l: any) => l.is_active !== false)
          .map((l: any) => ({ id: l.id, name: l.name, city: l.city }))
      );
    } catch {
      setSedes([]);
    }
  };

  const loadAppointments = async (professionalId: number) => {
    try {
      debugLog('Cargando citas del profesional:', professionalId);
      
      const response = await apiRequest(`/api/appointments/professional/${professionalId}`);
      
      if (response.success && Array.isArray(response.appointments)) {
        const appointmentsData = response.appointments.map((apt: any) => ({
          ...apt,
          client_display_name: apt.client_display_name || apt.client_name || 'Cliente sin nombre',
          service_name: apt.service_name || 'Servicio no especificado',
          urgency_level: apt.urgency_level || 'normal'
        }));
        
        debugLog('Citas cargadas exitosamente:', appointmentsData.length);
        setAppointments(appointmentsData);
        return appointmentsData;
      } else {
        debugLog('No se encontraron citas');
        setAppointments([]);
        return [];
      }
    } catch (error: any) {
      debugLog('Error cargando citas:', error);
      throw new Error(`Error cargando citas: ${error.message}`);
    }
  };

  const loadProfessionalSchedule = async (professionalId: number) => {
  try {
    debugLog('Cargando horarios del profesional:', professionalId);
    
    const response = await apiRequest(`/api/schedules/professional/${professionalId}`);
    
    if (response.success && Array.isArray(response.schedules)) {
      debugLog('Horarios cargados exitosamente:', {
        count: response.schedules.length,
        source: response.source,
        schedules: response.schedules.map((s: any) => ({
          day: s.day_of_week,
          working: s.is_working || s.is_available,
          hours: `${s.start_time}-${s.end_time}`
        }))
      });
      
      const schedulesForCalendar = response.schedules.map((schedule: any) => ({
        id: schedule.id || Math.random(),
        professional_id: schedule.professional_id,
        day_of_week: schedule.day_of_week,
        is_available: schedule.is_working || schedule.is_available || false,
        start_time: schedule.start_time || '09:00',
        end_time: schedule.end_time || '18:00',
        break_start_time: schedule.break_start_time || schedule.break_start,
        break_end_time: schedule.break_end_time || schedule.break_end
      }));
      
      debugLog('Horarios convertidos para calendario:', schedulesForCalendar.map((s: ProfessionalSchedule) => 
        `Día ${s.day_of_week}: ${s.is_available ? 'Disponible' : 'No disponible'} (${s.start_time}-${s.end_time})`
      ));
      
      setSchedule(schedulesForCalendar);
      return schedulesForCalendar;
      
    } else {
      debugLog('No se encontraron horarios configurados — calendario vacío');
      setSchedule([]);
      return [];
    }
  } catch (error: any) {
    debugLog('Error cargando horarios:', error);
    console.error('❌ Error cargando horarios del profesional:', error);

    setSchedule([]);
    return [];
  }
};

  const loadCalendarData = async (professionalId: number) => {
    setLoading(true);
    setError(null);

    try {
      debugLog('Iniciando carga completa de datos del calendario');
      
      await Promise.all([
        loadAppointments(professionalId),
        loadProfessionalSchedule(professionalId),
        loadLocations(professionalId)
      ]);

      debugLog('Datos cargados exitosamente');
      
    } catch (err: any) {
      debugLog('Error en carga de datos:', err);
      setError(`Error cargando datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FUNCIONES DE ACCIONES
  // =====================================================

  // Interceptar confirmación para turnos con transferencia pendiente
  const handleConfirmWithTransferCheck = (appointment: CalendarAppointment) => {
    if (
      appointment.status === 'pending' &&
      appointment.metodo_pago_elegido === 'transferencia' &&
      !appointment.pago_verificado
    ) {
      setPendingTransferAppointment(appointment);
      setShowTransferModal(true);
      return;
    }
    handleAppointmentAction(appointment.id, 'confirmed');
  };

  const handleAppointmentAction = async (appointmentId: number, action: string, data?: any) => {
    if (!user?.id) return;
    
    setActionLoading(true);
    
    try {
      debugLog(`Ejecutando acción ${action} en cita ${appointmentId}`);
      
      const response = await apiRequest(`/api/appointments/professional/${user.id}/appointment/${appointmentId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: action,
          professionalNotes: data?.professionalNotes,
          cancellationReason: data?.cancellationReason
        })
      });
      
      if (response.success) {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId 
            ? { ...apt, status: action as any, professional_notes: data?.professionalNotes }
            : apt
        ));
        
        setShowAppointmentDetail(false);
        setSelectedAppointment(null);
        
        debugLog(`Acción ${action} ejecutada exitosamente`);
      } else {
        throw new Error(response.message || 'Error actualizando cita');
      }
    } catch (error: any) {
      debugLog('Error ejecutando acción:', error);
      alert(`Error al ${action} la cita: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // "Borrar" un turno cancelado: soft-hide reversible (no se borra, queda registro)
  const handleHideAppointment = async () => {
    if (!selectedAppointment) return;
    setActionLoading(true);
    try {
      await apiRequest(`/api/appointments/appointment/${selectedAppointment.id}/hide`, { method: 'PATCH' });
      // Quitar de la vista al instante
      setAppointments(prev => prev.filter(apt => apt.id !== selectedAppointment.id));
      setConfirmHideOpen(false);
      setShowAppointmentDetail(false);
      setSelectedAppointment(null);
    } catch (error: any) {
      debugLog('Error ocultando turno:', error);
      alert('Error al borrar el turno');
    } finally {
      setActionLoading(false);
    }
  };

  const handleWhatsApp = (appointment: CalendarAppointment) => {
    if (!appointment.client_phone) return;
    
    const message = encodeURIComponent(
      `Hola ${appointment.client_display_name}, te contacto desde ${user?.business_name || `${user?.nombre} ${user?.apellido}`} sobre tu cita del ${formatLocalDate(appointment.appointment_date)} a las ${appointment.appointment_time}.`
    );
    const phone = appointment.client_phone.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  };

  // Crear sobreturno / nuevo turno
  const handleAddAppointment = (date: string, time: string) => {
    const hasConflict = appointments.some(apt => {
      const aptDate = apt.appointment_date.split('T')[0];
      const aptTime = apt.appointment_time.substring(0, 5);
      return aptDate === date && aptTime === time && apt.status !== 'cancelled';
    });
    const params = new URLSearchParams({ date, time });
    if (hasConflict) params.set('override', 'true');
    navigate(`/professional/new-appointment?${params.toString()}`);
  };

  // =====================================================
  // EFECTOS
  // =====================================================

  useEffect(() => {
    debugLog('Iniciando AdvancedCalendar...');
    
    // Si viene un usuario override (ej: asistente viendo calendario de un profesional)
    if (overrideUser) {
      debugLog('Usando overrideUser:', { id: overrideUser.id, nombre: overrideUser.nombre });
      setUser(overrideUser);
      setTheme(getThemeConfig(overrideUser.professional_type || 'belleza'));
      if (overrideUser.id) {
        loadCalendarData(overrideUser.id);
      }
      return;
    }

    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      debugLog('Usuario encontrado:', {
        id: parsedUser.id,
        professional_type: parsedUser.professional_type,
        especialidad: parsedUser.especialidad
      });
      
      setUser(parsedUser);
      setTheme(getThemeConfig(parsedUser.professional_type || 'belleza'));
      
      if (parsedUser.id) {
        loadCalendarData(parsedUser.id);
      }
    } else {
      debugLog('No hay usuario logueado, redirigiendo...');
      navigate('/login');
    }
  }, [navigate, overrideUser]);

  // Cargar configuración de sobreturnos
  useEffect(() => {
    if (!user?.id) return;
    const loadOverrideConfig = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/schedule/professional/${user.id}/config/public`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.scheduleConfig) {
            setMaxOverridesPerSlot(data.scheduleConfig.maxOverridesPerSlot || 0);
          }
        }
      } catch (err) { console.error('Error cargando config sobreturnos:', err); }
    };
    loadOverrideConfig();
  }, [user?.id]);

  // ✅ Cargar fechas con horarios bloqueados (para indicador en mes/lista)
  // Refrescar al volver al tab para ver cambios desde otros dispositivos
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    const loadBlockedDates = async () => {
      try {
        const data = await apiRequest(`/api/schedule/professional/${user.id}/exceptions`);
        if (cancelled) return;
        const exceptions = data.exceptions || data || [];
        const ymd = (val: any) => {
          if (!val) return '';
          if (typeof val === 'string') return val.split('T')[0];
          try {
            const d = new Date(val);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          } catch { return ''; }
        };
        const list = exceptions
          .filter((ex: any) => ex.exception_type === 'blocked_hours' && ex.exception_date)
          .map((ex: any) => ymd(ex.exception_date))
          .filter(Boolean);
        setBlockedDates(Array.from(new Set(list)));
      } catch (err) { console.error('Error cargando bloqueos:', err); }
    };
    loadBlockedDates();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') loadBlockedDates();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id]);

  // Obtener sedes únicas de los turnos
  const uniqueLocations = (() => {
    const locMap = new Map<number, { id: number; name: string; city?: string }>();
    appointments.forEach(apt => {
      if (apt.location_id && apt.location_name && !locMap.has(apt.location_id)) {
        locMap.set(apt.location_id, { id: apt.location_id, name: apt.location_name, city: apt.location_city });
      }
    });
    return Array.from(locMap.values());
  })();

  // Para el selector usamos las sedes ACTIVAS reales; si por algún motivo no
  // cargaron, caemos a las derivadas de las citas (comportamiento anterior).
  const sedeOptions = sedes.length > 0 ? sedes : uniqueLocations;

  useEffect(() => {
    let filtered = appointments;
    if (filterStatus !== 'todos') {
      filtered = filtered.filter(apt => apt.status === filterStatus);
    }
    if (filterLocation !== 'all') {
      filtered = filtered.filter(apt => apt.location_id === filterLocation);
    }
    setFilteredAppointments(filtered);
  }, [appointments, filterStatus, filterLocation]);

  useEffect(() => {
    let relevantAppointments = appointments;
    const isMonthView = viewMode === 'month';
    const isAgendaView = viewMode === 'agenda' || viewMode === 'day';
    
    if (isMonthView) {
      const monthPrefix = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      relevantAppointments = appointments.filter(apt => apt.appointment_date.startsWith(monthPrefix));
    } else if (isAgendaView) {
      const daySuffix = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      relevantAppointments = appointments.filter(apt => getDateOnly(apt.appointment_date) === daySuffix);
    } else {
      // Para viewMode 'list' u otros, mostrar los pendientes futuros o todos, aquí simplemente dejamos 'relevantAppointments'
    }

    const pending = relevantAppointments.filter(apt => apt.status === 'pending');
    const confirmed = relevantAppointments.filter(apt => apt.status === 'confirmed');
    const urgent = relevantAppointments.filter(apt => apt.urgency_level === 'high' && apt.status !== 'completed' && apt.status !== 'cancelled');
    const completed = relevantAppointments.filter(apt => apt.status === 'completed');
    const revenue = completed.reduce((sum, apt) => sum + apt.price, 0);

    setStats({
      todayAppointments: relevantAppointments.length, // Total para el periodo actual
      pendingAppointments: pending.length,
      confirmedAppointments: confirmed.length,
      totalRevenue: revenue,
      urgentCases: urgent.length
    });
  }, [appointments, viewMode, currentDate]);

  // =====================================================
  // FUNCIONES DE NAVEGACIÓN
  // =====================================================
  const navigateDate = (direction: 'prev' | 'next' | 'today') => {
    const newDate = new Date(currentDate);
   
    if (direction === 'today') {
      setCurrentDate(new Date());
      return;
    }
   
    const increment = direction === 'next' ? 1 : -1;
   
    switch (viewMode) {
      case 'month':
        newDate.setMonth(newDate.getMonth() + increment);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (7 * increment));
        break;
      case 'day':
        newDate.setDate(newDate.getDate() + increment);
        break;
    }
   
    setCurrentDate(newDate);
  };

  const getViewTitle = () => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: viewMode === 'day' ? 'numeric' : undefined
    };
   
    if (viewMode === 'week') {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
     
      return `${startOfWeek.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })} - ${endOfWeek.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}`;
    }
   
    return currentDate.toLocaleDateString('es-ES', options);
  };

  // =====================================================
  // HANDLERS DE MODAL
  // =====================================================
  const handleShowDayAppointments = (dayAppointments: CalendarAppointment[]) => {
    setSelectedDayAppointments(dayAppointments);
    setShowDayDetail(true);
  };

  const handleCloseDayDetail = () => {
    setShowDayDetail(false);
    setSelectedDayAppointments([]);
  };

  const handleShowAppointmentDetail = (appointment: CalendarAppointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetail(true);
  };

  const handleCloseAppointmentDetail = () => {
    setShowAppointmentDetail(false);
    setSelectedAppointment(null);
  };

  // =====================================================
  // RENDERS CONDICIONALES
  // =====================================================

  if (!user) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient} flex items-center justify-center`}>
        <div className="text-center">
          <p className="text-red-600">Error: Usuario no encontrado</p>
          <button 
            onClick={() => navigate('/login')}
            className={`mt-4 bg-gradient-to-r ${theme.gradient} text-white px-4 py-2 rounded-lg hover:shadow-lg transition-shadow`}
          >
            Ir a Login
          </button>
        </div>
      </div>
    );
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================

  return (
    <div className={`min-h-screen bg-gradient-to-br ${theme.bgGradient}`}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{theme.icons.calendar} {theme.title}</h1>
              <p className="text-gray-600">
                Gestiona tu agenda y citas profesionales
                {appointments.length > 0 && <span className="ml-2">• {appointments.length} citas totales</span>}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/professional/new-appointment')}
                className={`bg-gradient-to-r ${theme.gradient} text-white px-6 py-2 rounded-lg hover:shadow-lg transition-shadow flex items-center space-x-2`}
              >
                <span>{theme.icons.add}</span>
                <span>Nueva Cita</span>
              </button>
              <button 
                onClick={() => navigate(-1)} 
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
              >
                ← Volver
              </button>
            </div>
          </div>

          {/* Estadísticas rápidas (Cards Premium Glassmorphism) */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-4 border border-gray-100 flex flex-col items-center justify-center shadow-sm hover:shadow-md hover:shadow-blue-500/10 relative overflow-hidden group transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-indigo-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Calendar className="w-6 h-6 text-blue-500 mb-2 transition-transform group-hover:scale-110 duration-300" strokeWidth={1.5} />
              <div className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.todayAppointments}</div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center z-10">{viewMode === 'month' ? 'Turnos del Mes' : 'Turnos del Día'}</div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-4 border border-gray-100 flex flex-col items-center justify-center shadow-sm hover:shadow-md hover:shadow-emerald-500/10 relative overflow-hidden group transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <CheckCircle className="w-6 h-6 text-emerald-500 mb-2 transition-transform group-hover:scale-110 duration-300" strokeWidth={1.5} />
              <div className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.confirmedAppointments}</div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center z-10">Confirmados</div>
            </div>
            
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-4 border border-gray-100 flex flex-col items-center justify-center shadow-sm hover:shadow-md hover:shadow-amber-500/10 relative overflow-hidden group transition-all duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <Clock className="w-6 h-6 text-amber-500 mb-2 transition-transform group-hover:scale-110 duration-300" strokeWidth={1.5} />
              <div className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.pendingAppointments}</div>
              <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center z-10">Pendientes</div>
            </div>
          </div>

          {/* Controles de navegación */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigateDate('prev')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Anterior"
              >
                {theme.icons.prev}
              </button>
              
              <button
                onClick={() => navigateDate('today')}
                className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors font-medium"
              >
                Hoy
              </button>
              
              <button
                onClick={() => navigateDate('next')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Siguiente"
              >
                {theme.icons.next}
              </button>
              
              <h2 className="text-xl font-semibold text-gray-900 capitalize">
                {getViewTitle()}
              </h2>
            </div>

            <div className="flex items-center space-x-4">
              {/* Filtro de estado */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="todos">Todas las citas</option>
                <option value="pending">Pendientes ({stats.pendingAppointments})</option>
                <option value="confirmed">Confirmadas ({stats.confirmedAppointments})</option>
                <option value="completed">Completadas</option>
                <option value="cancelled">Canceladas</option>
              </select>

              {/* Filtro de sede (solo si hay más de una) */}
              {sedeOptions.length > 1 && (
                <select
                  value={filterLocation === 'all' ? 'all' : filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  className="px-3 py-2 border border-purple-200 bg-purple-50 text-purple-700 rounded-lg text-sm font-medium focus:ring-2 focus:ring-purple-300 focus:border-transparent"
                >
                  <option value="all">Todas las sedes</option>
                  {sedeOptions.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}{loc.city ? ` - ${loc.city}` : ''}</option>
                  ))}
                </select>
              )}

              {/* Selector de vista */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                {[
                  { id: 'agenda', label: 'Agenda', icon: <ClipboardList className="w-4 h-4" /> },
                  { id: 'month', label: 'Mes', icon: <Calendar className="w-4 h-4" /> },
                  { id: 'list', label: 'Lista', icon: <List className="w-4 h-4" /> }
                ].map(view => (
                  <button
                    key={view.id}
                    onClick={() => handleViewModeChange(view.id as ViewMode)}
                    className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center space-x-1 ${
                      viewMode === view.id
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {view.icon}
                    <span>{view.label}</span>
                  </button>
                ))}
              </div>

              {/* Botón de actualizar */}
              <button
                onClick={() => user?.id && loadCalendarData(user.id)}
                disabled={loading}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                {loading ? '🔄' : '↻'} Actualizar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Mensaje de error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-start">
              <span className="text-red-600 text-lg mr-3">⚠️</span>
              <div>
                <h4 className="font-medium text-red-800 mb-1">Error de conexión</h4>
                <p className="text-sm text-red-700">{error}</p>
                <button
                  onClick={() => user?.id && loadCalendarData(user.id)}
                  className="mt-3 bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vista del calendario */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {loading ? (
            <div className="py-12">
              <LoadingSpinner size={44} label="Cargando calendario..." />
            </div>
          ) : schedule.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No tenés horarios configurados</h3>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                Configurá tus días y horarios de atención para que tus pacientes puedan agendar turnos.
              </p>
              <button
                onClick={() => navigate('/professional/schedule-config')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                <Clock className="w-4 h-4" /> Configurar horarios
              </button>
            </div>
          ) : (
            <>
              {viewMode === 'agenda' && user && (
                <CalendarAgendaView
                  currentDate={currentDate}
                  onDateChange={setCurrentDate}
                  appointments={filteredAppointments}
                  schedules={schedule.map(s => ({
                    day_of_week: s.day_of_week,
                    start_time: s.start_time,
                    end_time: s.end_time,
                    break_start: (s as any).break_start_time,
                    break_end: (s as any).break_end_time,
                    is_working: s.is_available
                  }))}
                  user={{
                    id: user.id,
                    nombre: user.nombre,
                    apellido: user.apellido,
                    business_name: user.business_name,
                    professional_type: user.professional_type
                  }}
                  onAppointmentClick={handleShowAppointmentDetail}
                  onAppointmentAction={async (appointmentId, action, data) => {
                    await handleAppointmentAction(appointmentId, action, data);
                  }}
                  onRefresh={() => user?.id && loadCalendarData(user.id)}
                  loading={loading}
                />
              )}

              {viewMode === 'month' && (
                <CustomCalendar
                  currentDate={currentDate}
                  appointments={filteredAppointments}
                  schedule={schedule}
                  onDateClick={setSelectedDate}
                  onAppointmentClick={handleShowAppointmentDetail}
                  onShowDayAppointments={handleShowDayAppointments}
                  theme={theme}
                  maxOverridesPerSlot={maxOverridesPerSlot}
                  onAddAppointment={handleAddAppointment}
                  blockedDates={blockedDates}
                />
              )}
              
              {viewMode === 'list' && (
                <div className="space-y-4">
                  {/* Banner de horarios bloqueados */}
                  {blockedDates.length > 0 && (
                    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                      <span className="text-red-600 text-base leading-none mt-0.5">🚫</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-red-800">
                          {blockedDates.length === 1
                            ? 'Hay 1 día con horarios bloqueados'
                            : `Hay ${blockedDates.length} días con horarios bloqueados`}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {[...blockedDates].sort().slice(0, 8).map(d => (
                            <span key={d} className="text-[11px] bg-white text-red-700 border border-red-200 rounded px-1.5 py-0.5">
                              {d.split('-').reverse().join('/')}
                            </span>
                          ))}
                          {blockedDates.length > 8 && (
                            <span className="text-[11px] text-red-700">+{blockedDates.length - 8} más</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {filteredAppointments.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        {theme.icons.calendar}
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {filterStatus === 'todos' ? 'No hay citas programadas' : `No hay citas ${getStatusText(filterStatus).toLowerCase()}`}
                      </h3>
                      <p className="text-gray-600">
                        {filterStatus === 'todos' 
                          ? 'Tu agenda está libre en este momento'
                          : `No tienes citas con estado "${getStatusText(filterStatus).toLowerCase()}"`
                        }
                      </p>
                    </div>
                  ) : (
                    filteredAppointments
                      .sort((a, b) => `${a.appointment_date} ${a.appointment_time}`.localeCompare(`${b.appointment_date} ${b.appointment_time}`))
                      .map(apt => (
                        <div
                          key={apt.id}
                          onClick={() => handleShowAppointmentDetail(apt)}
                          className="border rounded-lg p-4 hover:shadow-sm cursor-pointer transition-shadow"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2">
                                <h3 className="font-medium text-gray-900">{apt.client_display_name}</h3>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyle(apt.status)}`}>
                                  {getStatusText(apt.status)}
                                </span>
                                {apt.is_first_time && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                                    Primera vez
                                  </span>
                                )}
                                {blockedDates.includes(getDateOnly(apt.appointment_date)) && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full font-semibold flex items-center gap-1">
                                    🚫 Día bloqueado
                                  </span>
                                )}
                                {apt.urgency_level === 'high' && (
                                  <span className="text-red-500">{theme.icons.urgent}</span>
                                )}
                              </div>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                                <div>
                                  <span className="font-medium">Fecha:</span> {formatLocalDate(apt.appointment_date)}
                                </div>
                                <div>
                                  <span className="font-medium">Hora:</span> {apt.appointment_time}
                                </div>
                                <div>
                                  <span className="font-medium">Servicio:</span> {getServiceDisplay(apt)}
                                </div>
                                <div>
                                  <span className="font-medium">Precio:</span> {formatPrice(apt.price)}
                                </div>
                                {apt.location_name && (
                                  <div>
                                    <span className="font-medium">Sede:</span> {apt.location_name}
                                  </div>
                                )}
                              </div>

                              {apt.notes && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <span className="font-medium">Notas:</span> {apt.notes}
                                </div>
                              )}

                              {/* Indicadores de notificaciones */}
                              <div className="flex items-center gap-3 mt-2 flex-wrap">
                                <NotificationIndicators appointment={apt} compact={true} />
                              </div>
                            </div>

                            <div className="flex items-center space-x-2">
                              {apt.client_phone && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleWhatsApp(apt);
                                  }}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Contactar por WhatsApp"
                                >
                                  <Smartphone className="w-5 h-5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de detalle de cita (Portal para escapar stacking context del header) */}
      {showAppointmentDetail && selectedAppointment && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ animation: 'modalFadeIn 0.2s ease-out forwards' }}>
          <style>{`
            @keyframes modalZoomIn {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
            @keyframes modalFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" style={{ animation: 'modalZoomIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
            <div className={`p-6 border-b border-gray-100 ${
              selectedAppointment.status === 'confirmed' ? 'bg-emerald-50/50' :
              selectedAppointment.status === 'pending' ? 'bg-amber-50/50' :
              selectedAppointment.status === 'cancelled' ? 'bg-red-50/50' : 'bg-gray-50/50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                   <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-sm ${
                     selectedAppointment.status === 'confirmed' ? 'bg-emerald-100 text-emerald-600' :
                     selectedAppointment.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                     selectedAppointment.status === 'cancelled' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                   }`}>
                     {selectedAppointment.status === 'confirmed' ? <CheckCircle className="w-5 h-5"/> : 
                      selectedAppointment.status === 'pending' ? <Clock className="w-5 h-5"/> : 
                      selectedAppointment.status === 'cancelled' ? <XCircle className="w-5 h-5"/> : <Calendar className="w-5 h-5"/>}
                   </div>
                   <div>
                     <h2 className="text-xl font-bold text-gray-900 tracking-tight">Detalle de la Cita</h2>
                     <p className="text-sm font-medium text-gray-500">{selectedAppointment.appointment_time.substring(0, 5)} - {getServiceDisplay(selectedAppointment)}</p>
                   </div>
                </div>
                <button
                  onClick={handleCloseAppointmentDetail}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-700 hover:bg-black/5 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto">
              {/* Información del cliente */}
              <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-5 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-xl text-gray-900 tracking-tight">
                    {selectedAppointment.client_display_name}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(selectedAppointment.status)}`}>
                      {getStatusText(selectedAppointment.status)}
                    </span>
                    {selectedAppointment.is_first_time && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        Primera vez
                      </span>
                    )}
                    {selectedAppointment.urgency_level === 'high' && (
                      <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center">
                        {theme.icons.urgent} Urgente
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedAppointment.client_email && (
                    <div>
                      <span className="font-medium text-gray-700">Email:</span>
                      <p className="text-gray-600">{selectedAppointment.client_email}</p>
                    </div>
                  )}
                  {selectedAppointment.client_phone && (
                    <div>
                      <span className="font-medium text-gray-700">Teléfono:</span>
                      <p className="text-gray-600">{selectedAppointment.client_phone}</p>
                    </div>
                  )}
                </div>

                {selectedAppointment.client_phone && (
                  <button
                    onClick={() => handleWhatsApp(selectedAppointment)}
                    className="mt-3 w-full bg-[#25D366] text-white py-2.5 px-4 rounded-xl hover:bg-[#128C7E] transition-colors shadow-sm flex items-center justify-center space-x-2 font-medium"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Contactar por WhatsApp</span>
                  </button>
                )}
              </div>

              {/* Información de la cita */}
              <div className="bg-gray-50/50 border border-gray-100 rounded-2xl p-5 mb-6">
                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Detalles del Servicio</h4>
                <div className="grid grid-cols-2 gap-y-5 gap-x-4 text-sm">
                  <div>
                    <span className="text-gray-500 block mb-1">Fecha</span>
                    <p className="text-gray-900 font-medium">{formatLocalDate(selectedAppointment.appointment_date)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">Hora</span>
                    <p className="text-gray-900 font-bold">{selectedAppointment.appointment_time.substring(0, 5)}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <span className="text-gray-500 block mb-1">Servicio</span>
                    <p className="text-gray-900 font-medium">{getServiceDisplay(selectedAppointment)}</p>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">Duración</span>
                    <p className="text-gray-900 font-medium">{selectedAppointment.duration} min</p>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">Precio</span>
                    <p className="text-gray-900 font-semibold">{formatPrice(selectedAppointment.price)}</p>
                  </div>
                  {selectedAppointment.deposit_required && (
                    <div>
                      <span className="text-gray-500 block mb-1">Seña</span>
                      <div className={`font-medium flex items-center gap-1.5 ${selectedAppointment.deposit_paid ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {selectedAppointment.deposit_paid ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                        <span>{formatPrice(selectedAppointment.deposit_amount || 0)}</span>
                        <span className="text-gray-400 text-xs font-normal">
                          ({selectedAppointment.deposit_paid ? 'Pagada' : 'Pendiente'})
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notas */}
              {/* Nota del turno: editable desde acá (se ve en toda la agenda) */}
              <div className="mb-6">
                <span className="font-medium text-gray-700">Nota del turno:</span>
                <AppointmentNoteEditor
                  appointmentId={selectedAppointment.id}
                  value={selectedAppointment.notes}
                  onSaved={(newNotes) => {
                    setSelectedAppointment((prev: any) => prev ? { ...prev, notes: newNotes } : prev);
                    setAppointments((prev: any[]) => prev.map(a =>
                      a.id === selectedAppointment.id ? { ...a, notes: newNotes } : a
                    ));
                  }}
                />
              </div>

              {selectedAppointment.professional_notes && (
                <div className="mb-6">
                  <span className="font-medium text-gray-700">Notas profesionales:</span>
                  <p className="text-gray-600 bg-blue-50 p-3 rounded-lg mt-1">{selectedAppointment.professional_notes}</p>
                </div>
              )}

              {/* Datos adicionales del paciente */}
              {selectedAppointment.patient_extra_data && Object.keys(selectedAppointment.patient_extra_data).length > 0 && (
                <div className="mb-6">
                  <span className="font-medium text-gray-700 block mb-2">Datos adicionales del paciente:</span>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                    {Object.entries(selectedAppointment.patient_extra_data).filter(([key]) => !['additional_service_ids', 'all_service_names'].includes(key)).map(([key, value]: [string, any]) => (
                      value && (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="font-bold text-gray-700">{getExtraLabel(key)}:</span>
                          <span className="text-gray-900">{formatExtraValue(key, value)}</span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Info de confirmación/cancelación */}
              {(selectedAppointment.confirmed_by || selectedAppointment.cancelled_by) && (
                <div className="mb-6 rounded-lg p-4 border" style={{
                  background: selectedAppointment.status === 'cancelled' ? '#FEF2F2' : '#F0FDF4',
                  borderColor: selectedAppointment.status === 'cancelled' ? '#FECACA' : '#BBF7D0'
                }}>
                  {selectedAppointment.confirmed_by && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-green-600 font-medium flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Confirmado por:</span>
                      <span className="text-green-800 font-semibold">
                        {selectedAppointment.confirmed_by === 'patient' ? 'Paciente (desde link)' :
                         selectedAppointment.confirmed_by === 'professional' ? 'Profesional' :
                         selectedAppointment.confirmed_by === 'assistant' ? 'Secretaria' : selectedAppointment.confirmed_by}
                      </span>
                      {selectedAppointment.confirmed_at && (
                        <span className="text-green-600 text-xs">
                          — {new Date(selectedAppointment.confirmed_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  )}
                  {selectedAppointment.cancelled_by && (
                    <div className={`flex flex-col gap-1 ${selectedAppointment.confirmed_by ? 'mt-2 pt-2 border-t border-red-200' : ''}`}>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-red-600 font-medium flex items-center gap-1"><XCircle className="w-4 h-4" /> Cancelado por:</span>
                        <span className="text-red-800 font-semibold">
                          {selectedAppointment.cancelled_by === 'patient' ? 'Paciente (desde link)' :
                           selectedAppointment.cancelled_by === 'professional' ? 'Profesional' :
                           selectedAppointment.cancelled_by === 'assistant' ? 'Secretaria' : selectedAppointment.cancelled_by}
                        </span>
                        {selectedAppointment.cancelled_at && (
                          <span className="text-red-500 text-xs">
                            — {new Date(selectedAppointment.cancelled_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      {selectedAppointment.cancellation_reason && (
                        <p className="text-red-700 text-sm bg-red-50 p-2 rounded mt-1">
                          Motivo: {selectedAppointment.cancellation_reason}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Indicadores de notificaciones */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Estado de notificaciones
                </h4>
                <NotificationIndicators appointment={selectedAppointment} compact={false} />
              </div>

              {/* Botón Sobreturno */}
              {maxOverridesPerSlot > 0 && selectedAppointment.status !== 'cancelled' && (
                <button
                  onClick={() => {
                    const date = selectedAppointment.appointment_date.split('T')[0];
                    const time = selectedAppointment.appointment_time.substring(0, 5);
                    setShowAppointmentDetail(false);
                    setSelectedAppointment(null);
                    handleAddAppointment(date, time);
                  }}
                  className="mb-4 w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-orange-50 hover:bg-orange-100 border border-orange-200 text-orange-700 font-medium transition-colors"
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold">+ST</span>
                  Agregar sobreturno a las {selectedAppointment.appointment_time.substring(0, 5)}
                </button>
              )}

              {/* Botones de acción */}
              <div className="flex flex-wrap gap-3">
                {selectedAppointment.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleConfirmWithTransferCheck(selectedAppointment)}
                      disabled={actionLoading}
                      className="flex-1 bg-green-100 text-green-800 py-3 px-4 rounded-lg hover:bg-green-200 transition-colors font-medium disabled:opacity-50"
                    >
                      {theme.icons.confirmed} Confirmar
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Motivo de cancelación (opcional):');
                        handleAppointmentAction(selectedAppointment.id, 'cancelled', { cancellationReason: reason });
                      }}
                      disabled={actionLoading}
                      className="flex-1 bg-red-100 text-red-800 py-3 px-4 rounded-lg hover:bg-red-200 transition-colors font-medium disabled:opacity-50"
                    >
                      {theme.icons.cancelled} Cancelar
                    </button>
                  </>
                )}
                
                {selectedAppointment.status === 'confirmed' && (
                  <>
                    <button
                      onClick={() => {
                        const notes = prompt('Notas del tratamiento (opcional):');
                        handleAppointmentAction(selectedAppointment.id, 'completed', { professionalNotes: notes });
                      }}
                      disabled={actionLoading}
                                            className="flex-1 bg-blue-100 text-blue-800 py-3 px-4 rounded-lg hover:bg-blue-200 transition-colors font-medium disabled:opacity-50"
                    >
                      {theme.icons.completed} Completar
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Motivo de cancelación:');
                        if (reason) {
                          handleAppointmentAction(selectedAppointment.id, 'cancelled', { cancellationReason: reason });
                        }
                      }}
                      disabled={actionLoading}
                      className="bg-red-100 text-red-800 py-3 px-4 rounded-lg hover:bg-red-200 transition-colors font-medium disabled:opacity-50"
                    >
                      {theme.icons.cancelled} Cancelar
                    </button>
                  </>
                )}

                {selectedAppointment.status === 'cancelled' && (
                  <button
                    onClick={() => setConfirmHideOpen(true)}
                    disabled={actionLoading}
                    className="flex-1 bg-red-50 text-red-700 border border-red-200 py-3 px-4 rounded-lg hover:bg-red-100 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <XCircle className="w-4 h-4" /> Borrar de la agenda
                  </button>
                )}

                {user?.professional_type === 'salud' && (
                  <button
                    onClick={() => {
                      const params = new URLSearchParams();
                      params.set('patient', selectedAppointment.client_display_name);
                      if (selectedAppointment.patient_extra_data) {
                        const extra = selectedAppointment.patient_extra_data;
                        if (extra.dni) params.set('dni', extra.dni);
                        if (Object.keys(extra).length > 0) {
                          sessionStorage.setItem('appointmentExtraData', JSON.stringify(extra));
                        }
                      }
                      if (selectedAppointment.client_email) params.set('email', selectedAppointment.client_email);
                      if (selectedAppointment.client_phone) params.set('phone', selectedAppointment.client_phone);
                      params.set('fromAppointment', 'true');
                      navigate(`/professional/clinical-records/new?${params.toString()}`);
                    }}
                    className="bg-purple-100 text-purple-800 py-3 px-4 rounded-lg hover:bg-purple-200 transition-colors font-bold flex items-center gap-2"
                  >
                    <ClipboardList className="w-4 h-4" />
                    Crear Historia Clinica
                  </button>
                )}

                {/* La receta se escribe durante la consulta: sin esto hay que
                    salir del calendario y retipear a alguien que está en
                    pantalla. Solo si la cuenta puede emitir — esta vista la
                    usan también estética y secretaria, y la receta la firma
                    el médico con su matrícula. */}
                {mostrarBotonReceta && (
                  <button
                    onClick={() => {
                      const params = new URLSearchParams({ nuevaReceta: '1' });
                      params.set('nombre', selectedAppointment.client_display_name || '');
                      const extra = selectedAppointment.patient_extra_data || {};
                      if (extra.dni) params.set('dni', String(extra.dni));
                      if (extra.sexo) params.set('sexo', String(extra.sexo));
                      if (extra.fecha_nacimiento) params.set('nacimiento', String(extra.fecha_nacimiento));
                      // Sin matrícula no se puede emitir, pero el botón se
                      // muestra igual: escondido, el médico nunca se entera de
                      // que existe. Lo lleva a cargarla, que es el camino que
                      // queremos.
                      navigate(
                        puedeEmitirRecetas
                          ? `/professional/prescriptions?${params.toString()}`
                          : '/professional/habilitar-recetas'
                      );
                    }}
                    className="bg-teal-100 text-teal-800 py-3 px-4 rounded-lg hover:bg-teal-200 transition-colors font-bold flex items-center gap-2"
                  >
                    <FileSignature className="w-4 h-4" />
                    Crear receta
                  </button>
                )}

                <button
                  onClick={handleCloseAppointmentDetail}
                  className="bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                  Cerrar
                </button>
              </div>

              {actionLoading && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Actualizando...</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmación de borrado (soft-hide reversible) */}
      <ConfirmModal
        open={confirmHideOpen}
        title="Borrar turno cancelado"
        message="Se quitará de la agenda este turno cancelado. Queda guardado por si necesitás recuperarlo."
        confirmLabel="Borrar"
        onConfirm={handleHideAppointment}
        onClose={() => setConfirmHideOpen(false)}
      />

      {/* Modal para mostrar todas las citas de un día (Portal) */}
      {showDayDetail && selectedDayAppointments.length > 0 && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ animation: 'modalFadeIn 0.2s ease-out forwards' }}>
          <style>{`
            @keyframes modalZoomIn {
              from { opacity: 0; transform: scale(0.95); }
              to { opacity: 1; transform: scale(1); }
            }
            @keyframes modalFadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
          `}</style>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" style={{ animation: 'modalZoomIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
            <div className="p-6 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight">
                    Citas del {selectedDate ? formatLocalDate(getDateOnly(selectedDayAppointments[0]?.appointment_date)) : formatLocalDate(getDateOnly(selectedDayAppointments[0]?.appointment_date))}
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">{selectedDayAppointments.length} citas programadas</p>
                </div>
                <button
                  onClick={handleCloseDayDetail}
                  className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto p-6">
              <div className="space-y-4">
                {selectedDayAppointments
                  .sort((a: CalendarAppointment, b: CalendarAppointment) => a.appointment_time.localeCompare(b.appointment_time))
                  .map((apt: CalendarAppointment) => (
                    <div
                      key={apt.id}
                      className="border rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                      onClick={() => {
                        handleShowAppointmentDetail(apt);
                        handleCloseDayDetail();
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-medium text-lg text-gray-900">{apt.client_display_name}</h3>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusStyle(apt.status)}`}>
                              {getStatusText(apt.status)}
                            </span>
                            {apt.is_first_time && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                Primera vez
                              </span>
                            )}
                            {apt.urgency_level === 'high' && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Urgente
                              </span>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">Hora:</span> {apt.appointment_time}
                            </div>
                            <div>
                              <span className="font-medium">Servicio:</span> {getServiceDisplay(apt)}
                            </div>
                            <div>
                              <span className="font-medium">Duración:</span> {apt.duration} min
                            </div>
                            <div>
                              <span className="font-medium">Precio:</span> {formatPrice(apt.price)}
                            </div>
                          </div>

                          {apt.notes && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">Notas:</span> {apt.notes}
                            </div>
                          )}

                          {/* Indicadores de notificaciones */}
                          <div className="flex items-center gap-3 mt-2 flex-wrap">
                            <NotificationIndicators appointment={apt} compact={true} />
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 ml-4">
                          {maxOverridesPerSlot > 0 && apt.status !== 'cancelled' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const date = apt.appointment_date.split('T')[0];
                                const time = apt.appointment_time.substring(0, 5);
                                setShowDayDetail(false);
                                handleAddAppointment(date, time);
                              }}
                              className="w-8 h-8 rounded-full bg-orange-500 hover:bg-orange-600 flex items-center justify-center text-white shadow-sm transition-all"
                              title="Agregar sobreturno"
                            >
                              <span className="text-[9px] font-bold">+ST</span>
                            </button>
                          )}
                          {apt.client_phone && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleWhatsApp(apt);
                              }}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Contactar por WhatsApp"
                            >
                              <Smartphone className="w-5 h-5" />
                            </button>
                          )}
                          <div className="text-right">
                            <div className="text-lg font-bold text-gray-900">
                              {apt.appointment_time}
                            </div>
                            <div className="text-xs text-gray-500">
                              {apt.duration} min
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Estadísticas del día */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Resumen del día</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-blue-600">
                      {selectedDayAppointments.length}
                    </div>
                    <div className="text-sm text-blue-800">Total Citas</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-green-600">
                      {selectedDayAppointments.filter((apt: CalendarAppointment) => apt.status === 'confirmed').length}
                    </div>
                    <div className="text-sm text-green-800">Confirmadas</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-3 text-center">
                    <div className="text-xl font-bold text-yellow-600">
                      {selectedDayAppointments.filter((apt: CalendarAppointment) => apt.status === 'pending').length}
                    </div>
                    <div className="text-sm text-yellow-800">Pendientes</div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button
                  onClick={handleCloseDayDetail}
                  className="bg-gray-100 text-gray-700 py-2 px-6 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Panel de debug en desarrollo */}
      {DEBUG_MODE && (
        <div className="fixed bottom-4 right-4 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-sm text-xs">
          <h4 className="font-semibold text-blue-800 mb-2">🔧 Debug Info</h4>
          <div className="space-y-1 text-blue-700">
            <div>Usuario ID: {user?.id}</div>
            <div>Citas: {appointments.length}</div>
            <div>Horarios: {schedule.length}</div>
            <div>Estado: {loading ? 'Cargando' : error ? 'Error' : 'OK'}</div>
            <div>Filtro: {filterStatus}</div>
            <div>Vista: {viewMode}</div>
            <div>Vista guardada: {localStorage.getItem('calendarViewMode')}</div>
          </div>
        </div>
      )}

      {/* Modal de verificación de transferencia */}
      {/* Modal de transferencia renderizado con Portal para estar por encima de todo */}
      {showTransferModal && pendingTransferAppointment && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.6)', padding: '1rem' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-4">
              <h3 className="text-lg font-bold text-amber-900 flex items-center gap-2">
                <Building2 className="w-5 h-5" /> Turno con Transferencia
              </h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-2">
                Este turno de <span className="font-semibold">{pendingTransferAppointment.client_display_name}</span> fue agendado con pago por <span className="font-semibold text-amber-700">transferencia bancaria</span>.
              </p>
              <p className="text-gray-600 text-sm mb-6">
                ¿Deseas ir al verificador de transferencias para confirmar el pago antes de confirmar el turno?
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setPendingTransferAppointment(null);
                    setShowAppointmentDetail(false);
                    setSelectedAppointment(null);
                    navigate('/professional/payment-verification');
                  }}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  <span className="flex items-center justify-center gap-2"><CheckCircle className="w-4 h-4" /> Sí, verificar transferencia</span>
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    const apt = pendingTransferAppointment;
                    setPendingTransferAppointment(null);
                    handleAppointmentAction(apt.id, 'confirmed');
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-4 rounded-xl font-medium transition-colors"
                >
                  Confirmar sin verificar pago
                </button>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setPendingTransferAppointment(null);
                  }}
                  className="w-full text-gray-500 hover:text-gray-700 py-2 text-sm transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdvancedCalendar;